import _ from "lodash";
/** external */
import React from "react";
import BarChartCustomizable from "UIComponents/BarChartCustomizable";
import { useI18n } from "Hooks";
import classNames from "classnames";
import { Tooltip, Avatar, EmptyState } from "@toddle-edu/ds-web";
import { CloseOutlined, TickLargeOutlined } from "@toddle-edu/ds-icons";
import { elevations, NoChartIllustration } from "@toddle-edu/ds-theme";
import QuestionBarChartTooltipContent from "../QuestionWiseInsights/QuestionBarChartTooltipContent";
/** relative */
import classes from "./QuestionInsightsField.scss";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { Trans } from "react-i18next";
import QuestionBlanksTooltip from "WorksheetInsights/components/QuestionWiseInsights/QuestionBlanksTooltip";
import { BLANKS_ANSWER_SEPARATOR } from "Worksheet/modules/WorksheetConstants";
import StudentAlerts from "AppComponents/StudentAlerts";
import { getStyleStrippedText } from "Utils";

const responsiveContainerProps = {
  width: "100%",
  height: 250,
};
const emptyStateSubtitleStyle = {
  maxWidth: "544px",
  lineHeight: "24px",
};

const QuestionInsightsField = ({ evaluationInsights }) => {
  const {
    showBarChart,
    barChartData,
    yAxisProps,
    cartisianGridProps,
    barChartStyle,
    barProps,
    xAxisProps,
    insightType,
    optionsList,
    studentCount,
    totalStudentCount,
    blanks,
    avgMarks,
  } = evaluationInsights;

  const { t } = useI18n(["worksheet"]);
  const blanksContainer = classNames("text-body-1", classes.blanksContainer);
  const { blanksObj, correctStudentLists, answers } = blanks || {};
  const uniqueAnswersClasses = classNames(
    classes.uniqueAnswers,
    "text-label-3"
  );
  const blanksInsightsClasses = classNames(
    classes.blanksInsights,
    "text-label-3"
  );
  const optionClasses = classNames("text-body-1", classes.option);

  const getInsightComponent = ({ insightType }) => {
    switch (insightType) {
      case "BLANKS":
        return (
          <div className={blanksContainer}>
            <div className={classes.startDivider}></div>
            {_.map(answers, (item, blankIndex) => {
              const insightResponse = _.get(blanksObj, blankIndex);
              const correctStudentList = _.get(correctStudentLists, blankIndex);

              return (
                <>
                  <div className={classes.blanksAnswerContainer}>
                    <div className={classes.blanksAnswer}>
                      {blankIndex + 1}.{" "}
                      {_.isEmpty(item) ? (
                        <span className={classes.noCorrectAnswer}>
                          {t("common:no_with_label", {
                            label: t("common:correct_ans"),
                            lowercase: true,
                          })}
                        </span>
                      ) : (
                        // Answer is in form of string "x;y;z" -> replacing with "x/y/z"
                        _.replace(
                          item,
                          new RegExp(BLANKS_ANSWER_SEPARATOR, "g"),
                          "/"
                        )
                      )}
                    </div>
                    <div>
                      <ConditionalWrapper
                        type={"wrapperAsElement"}
                        condition={_.size(correctStudentList) > 0}
                        wrapper={
                          <Tooltip
                            destroyTooltipOnScroll={false}
                            tooltip={
                              <QuestionBlanksTooltip
                                insightResponse={correctStudentList}
                                isUniqueAnswers={false}
                              />
                            }
                            showArrow={false}
                            theme={"light"}
                            placement={"bottom"}
                            overlayInnerStyle={{
                              padding: 0,
                              marginTop: "-12px",
                              marginLeft: "-18px",
                              width: "169px",
                            }}
                          />
                        }
                      >
                        <span>
                          {!_.isEmpty(item) ? (
                            <span className={blanksInsightsClasses}>
                              <Trans
                                i18nKey={t(
                                  "worksheet:label_fraction_with_count"
                                )}
                                label={t("worksheet:correctly_answered")}
                                count={_.size(correctStudentList)}
                                totalCount={studentCount}
                              >
                                <span>
                                  {{ count: _.size(correctStudentList) }}
                                </span>
                                /{{ totalCount: studentCount }}{" "}
                                {{ label: t("worksheet:correctly_answered") }}
                              </Trans>
                            </span>
                          ) : null}
                        </span>
                      </ConditionalWrapper>
                      <ConditionalWrapper
                        type={"wrapperAsElement"}
                        condition={_.size(insightResponse) > 0}
                        wrapper={
                          <Tooltip
                            destroyTooltipOnScroll={false}
                            tooltip={
                              <QuestionBlanksTooltip
                                insightResponse={insightResponse}
                                isUniqueAnswers={true}
                              />
                            }
                            showArrow={false}
                            theme={"light"}
                            placement={"bottom"}
                            overlayInnerStyle={{
                              opacity: 1,
                              padding: 0,
                              marginTop: "-12px",
                              marginLeft: "-18px",
                              width: "169px",
                              maxHeight: "108px",
                            }}
                          />
                        }
                      >
                        <span className={uniqueAnswersClasses}>
                          <Trans
                            i18nKey={t("common:label_count")}
                            label={t("worksheet:unique_answer")}
                            count={_.size(insightResponse)}
                          >
                            <span>{{ count: _.size(insightResponse) }}</span>{" "}
                            {{
                              label: t("worksheet:unique_answer", {
                                count: _.size(insightResponse),
                              }),
                            }}
                          </Trans>
                        </span>
                      </ConditionalWrapper>
                    </div>
                  </div>
                  {blankIndex < answers.length - 1 ? (
                    <div className={classes.divider}></div>
                  ) : null}
                </>
              );
            })}
          </div>
        );
      case "OPTIONS":
        return (
          <div>
            {_.map(optionsList, (option, key) => {
              return (
                <ConditionalWrapper
                  type={"wrapperAsElement"}
                  condition={_.size(option?.studentList) > 0}
                  wrapper={
                    <Tooltip
                      destroyTooltipOnScroll={false}
                      tooltip={
                        <div
                          className={classes.outerTooltipContainer}
                          style={elevations["elevation-2B"]}
                        >
                          <div className={classes.tooltipContainer}>
                            {_.map(option.studentList, student => {
                              const {
                                id,
                                studentName,
                                profileImage,
                                alertTypes = [],
                              } = student;

                              return (
                                <div className="text-body-3" key={id}>
                                  <div className={classes.avatarContainer}>
                                    <Avatar
                                      name={studentName}
                                      shade={"light"}
                                      src={profileImage}
                                      size="xx-small"
                                      className={classes.noShrink}
                                    />
                                    <span className={classes.avatarLabel}>
                                      {studentName}
                                    </span>
                                    <StudentAlerts
                                      studentId={id}
                                      alertTypes={alertTypes}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      }
                      showArrow={false}
                      theme={"light"}
                      placement={"bottomRight"}
                      overlayInnerStyle={{
                        padding: 0,
                        marginTop: "-32px",
                        background: "white",
                        borderRadius: "20px",
                      }}
                    />
                  }
                >
                  <div>
                    <div className={optionClasses}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getStyleStrippedText({
                            text: option.value,
                            type: "DOCUMENT_EDITOR",
                          }),
                        }}
                      ></div>
                      <Trans
                        i18nKey={"common:label_count"}
                        label={t("common:student")}
                        count={_.size(option?.studentList)}
                      >
                        <div className={classes.optionRightContainer}>
                          {{ count: _.size(option?.studentList) }}
                          <span>
                            {{
                              label: t("common:student", {
                                count: _.size(option?.studentList),
                              }),
                            }}
                          </span>

                          {option.isSelected ? (
                            <TickLargeOutlined variant="success" />
                          ) : (
                            <CloseOutlined variant="critical" />
                          )}
                        </div>
                      </Trans>
                    </div>
                    {!_.isEqual(key, _.size(optionsList) - 1) ? (
                      <div className={classes.divider} />
                    ) : (
                      <div style={{ paddingBottom: "16px" }}></div>
                    )}
                  </div>
                </ConditionalWrapper>
              );
            })}
          </div>
        );
      default:
        return showBarChart ? (
          <div className={classes.evaluationInsightsBarChartLabels}>
            <div className={classes.evaluationInsightsYAxisContainer}>
              <div className={classes.yAxisLabel}>
                {t("assessmentEvaluation:no_of_students")}
              </div>
              <div className={classes.evaluationInsightsBarChart}>
                <BarChartCustomizable
                  data={barChartData}
                  yAxisProps={yAxisProps}
                  barChartStyle={barChartStyle}
                  barProps={barProps}
                  showXAxis={true}
                  xAxisProps={xAxisProps}
                  showLegend={false}
                  showTooltip={true}
                  tooltipContent={QuestionBarChartTooltipContent}
                  cartisianGridProps={cartisianGridProps}
                  isResponsive={true}
                  responsiveContainerProps={responsiveContainerProps}
                  toolTipWrapperStyle={{ pointerEvents: "auto" }}
                />
              </div>
            </div>
            <div className={classes.xAxisLabel}>
              {t("progressReport:marks_obtained")}
            </div>
          </div>
        ) : (
          <div className={classes.emptyStateContainer}>
            <EmptyState
              illustration={NoChartIllustration}
              title={t("classRoom:no_insights_yet")}
              subtitle={t("classRoom:once_students_are_evaluated")}
              subtitleStyle={emptyStateSubtitleStyle}
            />
          </div>
        );
    }
  };
  const getLeftLabel = () => {
    switch (insightType) {
      case "OPTIONS":
        return t("worksheet:distribution_across_question_type", {
          questionType: "options",
        });
      case "BLANKS":
        return (
          <div>
            <div>
              {t("worksheet:distribution_across_question_type", {
                questionType: "answers",
              })}
            </div>
          </div>
        );
      default:
        return t("worksheet:score_distribution");
    }
  };
  return (
    <div
      className={classes.evaluationInsightsContainer}
      style={{
        paddingBottom: _.includes(["OPTIONS"], insightType) ? "0px" : "16px",
      }}
    >
      <div className={classes.evaluationInsightsHeader}>
        <div className={classes.evaluationInsightsLeftLabel}>
          {getLeftLabel()}
        </div>
        <div className={classes.evaluationInsightsRightLabel}>
          <Trans
            i18nKey={t("worksheet:label_fraction_with_count")}
            label={t("worksheet:attempted_by")}
            count={studentCount}
            totalCount={totalStudentCount}
          >
            <div>
              {{ label: t("worksheet:attempted_by") }}:{" "}
              <span>
                {{ count: studentCount }} /{" "}
                <span>{{ totalCount: totalStudentCount }}</span>
              </span>
            </div>
          </Trans>
          {showBarChart ? (
            <Trans
              i18nKey={t("common:label_with_score")}
              label={t("worksheet:average_marks")}
              score={avgMarks}
            >
              <div>
                {{ label: t("worksheet:average_marks") }}:{" "}
                <span>{{ score: avgMarks }}</span>
              </div>
            </Trans>
          ) : null}
        </div>
      </div>
      <div className={classes.container}>
        {getInsightComponent({ insightType })}
      </div>
    </div>
  );
};

export default QuestionInsightsField;
